import { ItemListProvider, useItemListContext } from '@/components/itemList/ItemListProvider';
import { SelectionBar } from '@/components/itemList/SelectionBar';
import { ItemCard } from '@/components/itemList/ItemCard';
import { ItemRow } from '@/components/itemList/ItemRow';
import { FileCard } from '@/components/itemList/FileCard';
import { FileRow } from '@/components/itemList/FileRow';
import { CreateButtons } from '@/components/itemList/CreateButtons';
import { ItemListModals } from '@/components/itemList/ItemListModals';
import { useSortedItems } from '@/hooks/useSortedItems';
import { useSession } from '@/hooks/useSession';
import type { ViewMode } from '@/hooks/useViewMode';
import type { NormalizedItem } from '@/components/itemList/types';

interface FolderItemListProps {
  items: NormalizedItem[];
  sortCriteriaIndex: number;
  sortAscending: boolean;
  onRefresh: () => void;
  currentFolderId: string;
  userRole?: string | null;
  viewMode?: ViewMode;
}

export function FolderItemList({
  items,
  sortCriteriaIndex,
  sortAscending,
  onRefresh,
  currentFolderId,
  userRole,
  viewMode = 'grid',
}: FolderItemListProps) {
  const { updateIsQuerying } = useSession();
  const sortedItems = useSortedItems(items, sortCriteriaIndex, sortAscending);
  const isRoot = currentFolderId === 'root';

  return (
    <ItemListProvider
      items={sortedItems}
      userRole={userRole}
      useCase="folder"
      parentId={currentFolderId}
      sortCriteria={sortCriteriaIndex}
      sortAscending={sortAscending}
      onRefresh={onRefresh}
      updateIsQuerying={updateIsQuerying}
    >
      <FolderItemListContent isRoot={isRoot} currentFolderId={currentFolderId} sortCriteriaIndex={sortCriteriaIndex} sortAscending={sortAscending} viewMode={viewMode} />
    </ItemListProvider>
  );
}

function FolderItemListContent({
  isRoot,
  currentFolderId,
  sortCriteriaIndex,
  sortAscending,
  viewMode,
}: {
  isRoot: boolean;
  currentFolderId: string;
  sortCriteriaIndex: number;
  sortAscending: boolean;
  viewMode: ViewMode;
}) {
  const { items: contextItems } = useItemListContext();
  const sortedItems = useSortedItems(contextItems, sortCriteriaIndex, sortAscending);

  return (
    <>
      <SelectionBar />

      {sortedItems.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">No items</div>
      ) : viewMode === 'grid' ? (
        <ul className="m-0 grid list-none gap-4 p-0 grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
          {sortedItems.map((item) => (
            <li key={item.id}>
              {item.itemtype === 'resource' ? (
                <FileCard item={item} parentId={currentFolderId} />
              ) : (
                <ItemCard item={item} />
              )}
            </li>
          ))}
        </ul>
      ) : (
        <ul className="m-0 list-none space-y-1 p-0">
          {sortedItems.map((item) =>
            item.itemtype === 'resource' ? (
              <FileRow key={item.id} item={item} variant="full" parentId={currentFolderId} />
            ) : (
              <ItemRow key={item.id} item={item} />
            ),
          )}
        </ul>
      )}

      <CreateButtons showUpload={!isRoot} showNewItem />
      <ItemListModals />
    </>
  );
}
